<script setup lang="ts">
/**
 * OnboardingCoachModal — 1:1 порт docs/mockups/modal-onboarding-coach.html
 * (inbox-184).
 *
 * Size sm overlay, multi-step (5-7 шагов). Подсвечивает элементы
 * интерфейса (spotlight overlay) и показывает coach-card с пояснением.
 * Состояние первого визита — persistent в localStorage + user profile.
 *
 * Payload: { steps?: CoachStep[] } (опционально кастомные steps)
 * Emit: 'onboarding-coach' { action: 'skip'|'done', stepIndex }
 */
import { computed, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiBadge from '@/components/ui/UiBadge.vue';

const modals = useModalStore();

interface CoachStep {
  title: string;
  body: string;
  target?: string;     // CSS selector подсвечиваемого элемента
  cta?: string;        // override default «Дальше» текст
}

const DEFAULT_STEPS: CoachStep[] = [
  { title: 'Добро пожаловать в short-link!', body: 'За 5 минут покажем главные возможности — где смотреть аналитику, как создать первую ссылку и где включить AI-копайлот.', cta: 'Поехали' },
  { title: 'Создайте первую ссылку',         body: 'Кнопка «+ Создать ссылку» в верхнем правом углу. Введите длинный URL — получите короткий с аналитикой кликов в реальном времени.', target: '[data-coach="create-link-btn"]' },
  { title: 'Кампании группируют ссылки',     body: 'В разделе «Кампании» можно сгруппировать ссылки по проекту и видеть агрегированную статистику. Полезно для performance-маркетинга.', target: '[data-coach="campaigns-nav"]' },
  { title: 'AI-копайлот делает анализ за вас', body: 'YandexGPT отвечает на вопросы про ваши ссылки на русском — «почему упал CTR», «лучшее время рассылки», «есть ли фрод».', target: '[data-coach="copilot-nav"]' },
  { title: 'Аналитика — за 1 клик',          body: 'В разделе «Аналитика» — готовые дашборды для performance / антифрода / конверсий. Можно собрать свой из 18 типов виджетов.', target: '[data-coach="analytics-nav"]' },
  { title: 'Интеграции с CRM и сервисами',   body: 'Bitrix24, amoCRM, Tinkoff, Yandex.Метрика, Mailchimp — настройка в 1 шаг. Полный список в разделе «Интеграции».', target: '[data-coach="integrations-nav"]' },
  { title: 'Готово! Удачи с маркетингом 🚀',  body: 'Если потребуется помощь — пишите info@link-platform.ru или нажмите ⌘+K для быстрого поиска по документации.', cta: 'Начать работу' },
];

const payload = computed<{ steps: CoachStep[] }>(() => {
  const raw = (modals.payload || {}) as Record<string, unknown>;
  const steps = Array.isArray(raw.steps) && (raw.steps as unknown[]).every((s) => typeof s === 'object' && s !== null)
    ? raw.steps as CoachStep[]
    : DEFAULT_STEPS;
  return { steps };
});

const stepIndex = ref(0);
const totalSteps = computed(() => payload.value.steps.length);
const currentStep = computed(() => payload.value.steps[stepIndex.value]!);
const isLast = computed(() => stepIndex.value === totalSteps.value - 1);

function next() {
  if (isLast.value) {
    finishCoach('done');
  } else {
    stepIndex.value++;
  }
}
function prev() {
  if (stepIndex.value > 0) stepIndex.value--;
}
function skip() {
  finishCoach('skip');
}
function finishCoach(action: 'skip' | 'done') {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('coach-completed', JSON.stringify({ action, completedAt: Date.now() }));
  }
  modals.emit('onboarding-coach', { action, stepIndex: stepIndex.value });
  modals.close();
}
</script>

<template>
  <UiModal :title="currentStep.title" size="sm" @close="skip">
    <template #header>
      <div class="head">
        <div class="head-top">
          <div class="step-icon">{{ stepIndex + 1 }}</div>
          <UiBadge variant="default">{{ stepIndex + 1 }} из {{ totalSteps }}</UiBadge>
        </div>
        <h2 class="modal-title">{{ currentStep.title }}</h2>
      </div>
    </template>

    <p class="coach-body">{{ currentStep.body }}</p>

    <div class="step-dots" aria-label="Шаги онбординга">
      <span
        v-for="(s, i) in payload.steps"
        :key="i"
        :class="['dot', { active: i === stepIndex, done: i < stepIndex }]"
      ></span>
    </div>

    <template #footer>
      <UiButton variant="ghost" @click="skip">Пропустить</UiButton>
      <div class="foot-spacer"></div>
      <UiButton v-if="stepIndex > 0" variant="outline" @click="prev">Назад</UiButton>
      <UiButton variant="primary" @click="next">
        {{ currentStep.cta || (isLast ? 'Готово' : 'Дальше') }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.head { display: flex; flex-direction: column; gap: 8px; }
.head-top { display: flex; align-items: center; gap: 10px; }
.step-icon {
  width: 32px; height: 32px; border-radius: 10px;
  background: linear-gradient(135deg, #4f8cff, #7c5cff);
  color: #fff; display: grid; place-items: center;
  font-weight: 800; font-size: 14px;
}
.modal-title { font-size: 17px; letter-spacing: -0.01em; margin: 0; }

.coach-body {
  font-size: 14px; color: var(--fg, #e6eaf0); line-height: 1.55; margin: 0 0 18px;
}

.step-dots {
  display: flex; gap: 6px; justify-content: center; padding: 4px 0 8px;
}
.dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  transition: 0.15s;
}
.dot.active { background: var(--accent, #4f8cff); border-color: var(--accent, #4f8cff); transform: scale(1.2); }
.dot.done { background: rgba(79,140,255,0.4); border-color: rgba(79,140,255,0.4); }

.foot-spacer { flex: 1; }
</style>
